import { useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from "@/components/ui/skeleton";
import { useProfileDrawer } from "@/contexts/ProfileDrawerContext";

interface NetworkButtonProps {
  userId: string;
  username: string;
}

export const NetworkButton = ({ userId, username }: NetworkButtonProps) => {
  const { openProfileDrawer } = useProfileDrawer();

  // Fetch all profiles for channel-based filtering
  const { data: allProfiles, isLoading } = useQuery({
    queryKey: ['all-profiles-network', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, username, avatar_url, title, organization, organizations')
        .order('display_name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes - profiles don't change frequently
  });

  // Fetch current user's profile for channel detection
  const { data: currentProfile } = useQuery({
    queryKey: ['current-profile-network', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('organizations, display_name')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId
  });

  // Filter profiles based on channel membership
  const getNetworkProfiles = () => {
    if (!allProfiles || !currentProfile) return [];

    // Trey sees EVERYONE (MVP testing)
    if (username === 'trey-fleming') {
      return allProfiles;
    }

    const userOrgs = Array.isArray(currentProfile.organizations) ? currentProfile.organizations : [];
    const isIspaiMember = userOrgs.includes('iSPAI');
    const isCurtMember = userOrgs.includes('CURT');

    const filteredProfiles = allProfiles.filter(p => {
      if (p.id === userId) return false; // Exclude self
      
      const memberOrgs = Array.isArray(p.organizations) ? p.organizations : [];
      
      // Cross-channel members (e.g., Pete Dumont)
      if (isIspaiMember && isCurtMember) {
        return memberOrgs.includes('iSPAI') || memberOrgs.includes('CURT');
      }
      
      // iSPAI-only members
      if (isIspaiMember) {
        return memberOrgs.includes('iSPAI');
      }
      
      // CURT-only members
      if (isCurtMember) {
        return memberOrgs.includes('CURT');
      }
      
      return false;
    });
    
    return filteredProfiles;
  };

  const networkProfiles = getNetworkProfiles();

  // Preload avatar images for faster display
  useEffect(() => {
    if (networkProfiles.length > 0) {
      networkProfiles.forEach(profile => {
        if (profile.avatar_url) {
          const img = new Image();
          img.src = profile.avatar_url;
        }
      });
    }
  }, [networkProfiles]);

  // Determine header text
  const getHeaderText = () => {
    const firstName = currentProfile?.display_name?.split(' ')[0] || 'Your';
    return `${firstName}'s Knowledge Network`;
  };

  const getSubheaderText = () => {
    const userOrgs = Array.isArray(currentProfile?.organizations) ? currentProfile.organizations : [];
    const isIspaiMember = userOrgs.includes('iSPAI');
    const isCurtMember = userOrgs.includes('CURT');
    
    if (isIspaiMember && isCurtMember) {
      return `iSPAI + CURT Channels (${networkProfiles.length} members)`;
    }
    
    if (isIspaiMember) {
      return `iSPAI Channel (${networkProfiles.length} members)`;
    }
    
    if (isCurtMember) {
      return `CURT Channel (${networkProfiles.length} members)`;
    }
    
    return '';
  };

  return (
    <>
      <HoverCard>
        <HoverCardTrigger asChild>
          <Button size="sm" className="w-14 bg-green-600 text-white hover:bg-green-700">
            NW
          </Button>
        </HoverCardTrigger>
        <HoverCardContent className="w-[600px]">
          <ScrollArea className="max-h-[600px]">
            <h4 className="font-semibold mb-2">{getHeaderText()}</h4>
            {getSubheaderText() && (
              <p className="text-sm text-muted-foreground mb-3">
                {getSubheaderText()}
              </p>
            )}
            
            {isLoading ? (
              <div className="grid grid-cols-8 gap-2">
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} className="w-12 h-12 rounded-full" />
                ))}
              </div>
            ) : networkProfiles.length > 0 ? (
              <div className="grid grid-cols-8 gap-2">
                <TooltipProvider>
                  {networkProfiles.map((member, index) => (
                    <Tooltip key={member.id}>
                      <TooltipTrigger asChild>
                        <Avatar 
                          className="cursor-pointer hover:ring-2 hover:ring-green-700 transition-all w-12 h-12"
                          onClick={() => openProfileDrawer(member.id)}
                        >
                          <AvatarImage 
                            src={member.avatar_url} 
                            alt={member.display_name}
                            className="object-cover"
                            loading={index < 16 ? "eager" : "lazy"}
                          />
                          <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-sm">
                            {member.display_name[0]}
                          </AvatarFallback>
                        </Avatar>
                      </TooltipTrigger>
                      <TooltipContent className="w-80 p-3">
                        <div>
                          <p className="font-semibold">{member.display_name}</p>
                          {member.title && <p className="text-xs text-gray-700">{member.title}</p>}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </TooltipProvider>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No network members in your channels yet.
              </p>
            )}
          </ScrollArea>
        </HoverCardContent>
      </HoverCard>
    </>
  );
};